<template>
  <div class="step-content">
    <h5 class="mb-3">
      <i class="feather icon-paperclip mr-2"></i>
      Anexos Complementares
    </h5>
    <p class="text-muted mb-4">
      Anexe documentos complementares que possam ser solicitados durante o processo de análise (opcional).
    </p>

    <b-alert variant="info" show class="mb-4">
      <div class="alert-body">
        <h6 class="alert-heading">
          <i class="feather icon-info mr-1"></i>
          Informações sobre Anexos
        </h6>
        <hr>
        <ul class="mb-0">
          <li><strong>Formatos aceitos:</strong> PDF, DOC, DOCX, XLS, XLSX, JPG, PNG</li>
          <li><strong>Tamanho máximo:</strong> 10MB por arquivo</li>
          <li><strong>Quantidade:</strong> Você pode anexar múltiplos arquivos</li>
          <li><strong>Opcional:</strong> Este step pode ser pulado se não houver documentos adicionais</li>
        </ul>
      </div>
    </b-alert>

    <!-- Upload de Múltiplos Arquivos -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Anexos Complementares (se solicitado)">
          <b-form-file
            :value="null"
            multiple
            placeholder="Clique para selecionar os arquivos ou arraste aqui..."
            drop-placeholder="Solte os arquivos aqui..."
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            :state="getFieldState('additional_attachments')"
            :disabled="disabled"
            @input="handleFileUpload"
            class="mb-3"
          />
          <small class="form-text text-muted">
            <i class="feather icon-upload mr-1"></i>
            Selecione um ou mais arquivos para anexar. Todos os anexos são opcionais.
          </small>
          <b-form-invalid-feedback>
            Erro no upload dos anexos
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Lista de arquivos carregados -->
    <div v-if="hasUploadedFiles" class="uploaded-files mt-4">
      <h6 class="text-muted mb-3">
        <i class="feather icon-check-circle mr-1"></i>
        Arquivos Carregados
      </h6>
      <b-card>
        <b-list-group>
          <b-list-group-item
            v-for="(file, index) in formData.additional_attachments"
            :key="index"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <i class="feather icon-file-text text-danger mr-2"></i>
              <a v-if="getFileDownloadUrl(file)" :href="getFileDownloadUrl(file)" target="_blank" class="mr-2 download-link text-primary" title="Baixar arquivo">
                <i class="feather icon-download"></i>
              </a>
              <strong>{{ file.metadata.original_name }}</strong>
              <div class="text-muted small">
                {{ getDocumentTypeLabel(file.metadata.document_type) }}
                <small class="ml-2">({{ formatFileSize(file.size) }})</small>
                <small class="ml-2 text-muted">[{{ getFileExtension(file.file_name) }}]</small>
              </div>
            </div>
            <b-button
              v-if="!disabled"
              variant="outline-danger"
              size="sm"
              @click="removeFile(index)"
            >
              <i class="feather icon-trash-2"></i>
            </b-button>
          </b-list-group-item>
        </b-list-group>
      </b-card>
    </div>

    <!-- Mensagem quando não há anexos -->
    <div v-else class="no-attachments text-center p-4 border rounded bg-light">
      <i class="feather icon-folder text-muted" style="font-size: 2rem;"></i>
      <p class="text-muted mt-2 mb-0">
        Nenhum anexo foi adicionado ainda.<br>
        <small>Este passo é opcional e pode ser pulado se não houver documentos complementares.</small>
      </p>
    </div>

    <b-alert variant="success" show class="mt-4" v-if="hasUploadedFiles">
      <div class="alert-body">
        <h6 class="alert-heading">
          <i class="feather icon-check mr-1"></i>
          Anexos Prontos
        </h6>
        <hr>
        <p class="mb-0">
          {{ attachmentCount }} arquivo(s) anexado(s) com sucesso.
          Você pode adicionar mais arquivos ou continuar para o próximo passo.
        </p>
      </div>
    </b-alert>

    <!-- Modal de Confirmação de Remoção -->
    <b-modal
      v-model="showRemoveConfirmModal"
      title="Confirmar Remoção"
      centered
      hide-footer
      @hide="cancelRemove"
    >
      <div class="text-center">
        <i class="feather icon-trash-2 text-danger mb-2" style="font-size: 2rem;"></i>
        <p>
          Tem certeza que deseja remover este arquivo?
        </p>
      </div>
      <div class="text-center mt-3">
        <b-button variant="danger" class="mr-2" @click="confirmRemove">
          Remover
        </b-button>
        <b-button variant="secondary" @click="cancelRemove">
          Cancelar
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormFile,
  BFormInvalidFeedback,
  BAlert,
  BCard,
  BButton,
  BListGroup,
  BListGroupItem
} from 'bootstrap-vue'
import CebasCredentialingService from '@/services/api/cebas/CebasCredentialingService'

export default {
  name: 'AdditionalAttachmentsStep',
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormFile,
    BFormInvalidFeedback,
    BAlert,
    BCard,
    BButton,
    BListGroup,
    BListGroupItem
  },
  props: {
    formData: {
      type: Object,
      required: true,
      default: () => ({
        additional_attachments: []
      })
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    link: {
      type: String,
      required: true
    },
    credentialingId: {
      type: [String, Number],
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:form-data', 'upload-document', 'remove-document'],
  computed: {
    hasUploadedFiles() {
      return this.formData && this.formData.additional_attachments && this.formData.additional_attachments.length > 0;
    },
    attachmentCount() {
      return this.formData && this.formData.additional_attachments ? this.formData.additional_attachments.length : 0;
    }
  },
  data() {
    return {
      showRemoveConfirmModal: false,
      fileToRemove: null,
      removeIndex: null
    }
  },
  methods: {
    getFieldState(fieldName) {
      if (this.errors[fieldName]) {
        return false;
      }
      return null;
    },

    async handleFileUpload(files) {
      if (!files || files.length === 0) return;

      // Converter FileList para Array
      const newFiles = Array.from(files);
      const currentAttachments = this.formData.additional_attachments || [];

      // Validar tamanho dos arquivos (máximo 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB em bytes
      const validFiles = newFiles.filter(file => {
        if (file.size > maxSize) {
          this.$vs.notify({
            title: 'Arquivo muito grande',
            text: `O arquivo "${file.name}" excede o tamanho máximo de 10MB.`,
            color: 'warning'
          });
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        // Adicionar arquivos ao formData com status de upload iniciado
        const updatedAttachments = [...currentAttachments];

        for (const file of validFiles) {
          const fileData = {
            file,
            uploaded: false,
            metadata: {
              original_name: file.name,
              size: file.size,
              type: file.type,
              last_modified: file.lastModified,
              document_type: 'additional_attachment'
            }
          };

          updatedAttachments.push(fileData);

          // Emitir evento de upload para o componente pai
          this.$emit('upload-document', {
            fieldName: 'additional_attachments',
            file,
            metadata: fileData.metadata,
            credentialingId: this.credentialingId
          });
        }

        this.updateField('additional_attachments', updatedAttachments);

        this.$vs.notify({
          title: 'Arquivos anexados',
          text: `${validFiles.length} arquivo(s) adicionado(s) com sucesso.`,
          color: 'success'
        });
      }
    },

    async removeFile(index) {
      try {
        this.fileToRemove = this.formData.additional_attachments[index];
        this.removeIndex = index;
        this.showRemoveConfirmModal = true;
      } catch (error) {
        console.error('Error removing file:', error);
        this.$vs.notify({
          title: 'Erro',
          text: 'Erro ao remover arquivo. Tente novamente.',
          color: 'danger',
          position: 'top-right'
        });
      }
    },

    async confirmRemove() {
      try {
        if (!this.fileToRemove) return;

        // Emite evento para o componente pai remover o arquivo
        this.$emit('remove-document', {
          mediaId: this.fileToRemove.id,
          documentId: this.fileToRemove.model_id,
          index: this.removeIndex,
          metadata: this.fileToRemove.metadata
        });

        // Fecha o modal e reseta o estado
        this.showRemoveConfirmModal = false;
        this.fileToRemove = null;
        this.removeIndex = null;
      } catch (error) {
        console.error('Error removing file:', error);
      }
    },

    cancelRemove() {
      this.showRemoveConfirmModal = false;
      this.fileToRemove = null;
      this.removeIndex = null;
    },

    updateField(fieldName, value) {
      this.$emit('update:form-data', { [fieldName]: value });
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    getFileIcon(fileName) {
      if (!fileName || typeof fileName !== 'string') {
        return 'feather icon-file text-muted';
      }

      const extension = fileName.split('.').pop().toLowerCase();
      const iconMap = {
        'pdf': 'feather icon-file-text text-danger',
        'doc': 'feather icon-file-text text-primary',
        'docx': 'feather icon-file-text text-primary',
        'xls': 'feather icon-file text-success',
        'xlsx': 'feather icon-file text-success',
        'jpg': 'feather icon-image text-info',
        'jpeg': 'feather icon-image text-info',
        'png': 'feather icon-image text-info'
      };

      return iconMap[extension] || 'feather icon-file text-muted';
    },

    getDocumentTypeLabel(type) {
      const labels = {
        additional_attachment: 'Outros Documentos'
      }
      return labels[type] || type
    },

    getFileExtension(filename) {
      if (!filename) return '';
      const parts = filename.split('.');
      return parts.length > 1 ? parts.pop().toUpperCase() : '';
    },

    getFileDownloadUrl(file) {
      if (!file) return null;

      // Se é um arquivo que foi enviado (tem url_tmp)
      if (file.url_tmp) {
        return file.url_tmp;
      }

      // Se é um arquivo que foi carregado do servidor (tem metadata)
      if (file.metadata && file.metadata.url_tmp) {
        return file.metadata.url_tmp;
      }

      // Verificar outras possíveis propriedades de URL
      if (file.url_temp) {
        return file.url_temp;
      }

      if (file.url) {
        return file.url;
      }

      if (file.download_url) {
        return file.download_url;
      }

      return null;
    },
  }
}
</script>

<style lang="scss" scoped>
.step-content {
  padding: 1rem 0;

  h5 {
    color: #333;
    font-weight: 600;

    .feather {
      color: var(--dynamic-primary, #156440);
    }
  }

  .alert {
    border-left: 4px solid var(--bs-info, #00cfe8);
  }
}

/* Inputs com foco usando cores dinâmicas */
.step-content .form-control:focus {
  border-color: var(--dynamic-primary, #156440) !important;
  box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
}

.step-content .form-control.is-valid {
  border-color: var(--bs-success, #28c76f) !important;
}

.step-content .form-control.is-invalid {
  border-color: var(--bs-danger, #ea5455) !important;
}

/* Estilos para upload de arquivos */
.step-content .custom-file-input:focus ~ .custom-file-label {
  border-color: var(--dynamic-primary, #156440);
  box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25);
}

/* Lista de anexos */
.uploaded-files .card {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
}

.uploaded-files ul li {
  display: flex;
  align-items: center;
  padding: 0.25rem 0;
}

.uploaded-files .feather {
  flex-shrink: 0;
}

/* Estilos para drag and drop */
.custom-file-input:focus ~ .custom-file-label {
  border-color: var(--bs-primary, #007bff);
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Indicadores visuais para diferentes tipos de arquivo */
.text-success {
  color: #28a745 !important;
}

.text-warning {
  color: #ffc107 !important;
}

/* Estilo para quando não há anexos */
.no-attachments {
  border: 2px dashed #dee2e6 !important;

  .feather {
    opacity: 0.5;
  }
}

/* Estilos para os links de download */
.download-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: opacity 0.2s ease;
  vertical-align: middle;

  &:hover {
    opacity: 0.7;
    text-decoration: none;
  }

  i {
    font-size: 14px;
  }
}

/* Responsividade */
@media (max-width: 768px) {
  .uploaded-files ul li {
    flex-wrap: wrap;

    .btn-remove {
      margin-top: 0.5rem;
    }
  }
}
</style>
